/**
 * Canvas3DFootprintLayer — shows the owner's published 3D scene ON the 2D
 * Canvas as its BIRD'S-EYE (top-down) projection, and lets the owner DRAG it to
 * reposition the REAL 3D placement (X,Y) inside the owned footprint. Dragging is
 * validated + persisted by the parent through the certified 3D Draft authority,
 * so the 2D Canvas and the 3D scene stay coherent. A ⟳ affordance rotates the
 * whole scene 90°. Rendered as an absolute overlay (like the Frozen Artwork
 * layer); only the footprint itself is interactive so pan/select pass through.
 */
import { useEffect, useMemo, useRef } from "react";

import { sceneBoundsXY, topDownProjection, type WorkArt } from "../../domain/voxel3d";

function ProjectionCanvas({
  workArts, sw, sh, minX, minY,
}: {
  workArts: WorkArt[];
  sw: number;
  sh: number;
  minX: number;
  minY: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const cells = useMemo(() => topDownProjection(workArts), [workArts]);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, sw, sh);
    for (const c of cells) {
      ctx.fillStyle = c.color;
      ctx.fillRect(c.x - minX, c.y - minY, 1, 1);
    }
  }, [cells, sw, sh, minX, minY]);
  return (
    <canvas
      ref={ref}
      width={sw}
      height={sh}
      style={{ width: "100%", height: "100%", imageRendering: "pixelated", display: "block", pointerEvents: "none", position: "relative", zIndex: 1 }}
    />
  );
}

export function Canvas3DFootprintLayer({
  workArts, w2s, scale, interactive, onDragStart, onDrag, onDragEnd, onRotate,
}: {
  workArts: WorkArt[];
  w2s: (x: number, y: number) => { x: number; y: number };
  scale: number;
  interactive: boolean;
  onDragStart?: () => void;
  onDrag?: (dx: number, dy: number) => void;
  onDragEnd?: () => void;
  onRotate?: () => void;
}) {
  const bounds = useMemo(() => sceneBoundsXY(workArts), [workArts]);
  const dragRef = useRef<{ sx: number; sy: number } | null>(null);
  if (!bounds) return null;

  const sw = bounds.maxX - bounds.minX + 1;
  const sh = bounds.maxY - bounds.minY + 1;
  const s = w2s(bounds.minX, bounds.minY);
  const w = sw * scale;
  const h = sh * scale;

  const onDown = (e: React.PointerEvent) => {
    if (!interactive) return;
    e.stopPropagation();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    dragRef.current = { sx: e.clientX, sy: e.clientY };
    onDragStart?.();
  };
  const onMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    onDrag?.(Math.round((e.clientX - d.sx) / scale), Math.round((e.clientY - d.sy) / scale));
  };
  const onUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    dragRef.current = null;
    onDragEnd?.();
  };

  return (
    <div data-testid="canvas-3d-footprint-layer" className="pointer-events-none absolute inset-0">
      <div
        data-testid="canvas-3d-footprint"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        className={`group absolute ${interactive ? "pointer-events-auto cursor-move" : ""}`}
        style={{ left: s.x, top: s.y, width: Math.max(4, w), height: Math.max(4, h), touchAction: "none" }}
      >
        <ProjectionCanvas workArts={workArts} sw={sw} sh={sh} minX={bounds.minX} minY={bounds.minY} />
        <span aria-hidden className="absolute inset-0 rounded-[2px] ring-1 ring-cyan-300/70 group-hover:ring-2 group-hover:ring-cyan-300" style={{ borderStyle: "dashed" }} />
        {interactive && (
          <div className="absolute -top-6 left-0 flex items-center gap-1">
            <span data-testid="canvas-3d-badge" className="rounded-sm bg-cyan-400/90 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-black">
              3D · trascina
            </span>
            <button
              data-testid="canvas-3d-rotate"
              type="button"
              title="Ruota 90°"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onRotate?.(); }}
              className="rounded-sm bg-cyan-400/90 px-1 py-0.5 font-mono text-[10px] leading-none text-black hover:bg-cyan-300"
            >
              ⟳
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
